import { createContext } from '@chakra-ui/react-context';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { SavedListCreateValuesType } from 'ui-components';
import { queryClient } from '~/config/reactQueryConfig';
import { FEATURE_FLAGS } from '~/constants';
import { useKeyup } from '~/core/hooks';
import { useCreateList, useGetSavedLists } from '~/hooks/api/useSavedLists';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { selectTalentManager } from '~/selectors/talent-manager';
import { ManagerType } from '~/types';

import { PAGE_SIZE, STEP, TYPE } from './SavedListsModal.constants';
import {
  mapCreatorFromFoamToUI,
  mapSavedListFromFoamToUI,
} from './SavedListsModal.mapper';
import { useSavedListStateStore } from './SavedListsModal.store';
import {
  SavedListsModalProps,
  StepValues,
  TypeValues,
} from './SavedListsModal.types';

const { foamSavedListAgencies } = FEATURE_FLAGS;

export const useSavedListsModal = ({
  creators = [],
  isOpen,
  onClose,
  onSuccess,
}: SavedListsModalProps) => {
  const [listQuery, setListQuery] = useState<string>();
  const [renderCreateItem, setRenderCreateItem] = useState(false);
  const [step, setStep] = useState<StepValues>(STEP.step1);
  const [type, setType] = useState<TypeValues>(TYPE.agency);
  const { clearList } = useSavedListStateStore();
  const { isEnabled: hasAgenciesEnabled } = useFeature(foamSavedListAgencies);
  const { mutateAsync: mutateCreateList, isLoading: isCreateListLoading } =
    useCreateList({ disableInvalidation: true });
  const { updateList } = useSavedListStateStore();
  const manager: ManagerType | undefined = useSelector(selectTalentManager);

  const isAgencyListEnabled = !!manager?.agency?.enabled && hasAgenciesEnabled;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isInitialLoading,
    isLoading,
    isRefetching,
    queryKey,
  } = useGetSavedLists(
    {
      enableAgencyLists: type === TYPE.agency && isAgencyListEnabled,
      name: listQuery ? [listQuery] : undefined,
      pageSize: PAGE_SIZE,
      saved: true,
      properties: [
        'agencyName',
        'category',
        'createdAt',
        'firstFourCreators',
        'id',
        'listOrder',
        'listOrderAgency',
        'manager',
        'name',
        'totalCreators',
      ],
    },
    {
      enabled: isOpen,
    },
  );

  const lists = useMemo(() => {
    if (data?.results) {
      return mapSavedListFromFoamToUI(data.results, type === TYPE.agency);
    }
    return [];
  }, [data, type]);

  const handleClose = useCallback(() => {
    setListQuery('');
    setStep(STEP.step1);
    clearList();
    onClose();
  }, [setListQuery, setStep, clearList, onClose]);

  const handleFetchNextPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const handleSubmitCreateList = useCallback(
    async ({ name }: SavedListCreateValuesType) => {
      await mutateCreateList(
        {
          creatorIds: [],
          name,
          saved: true,
        },
        {
          onSuccess: async (newList) => {
            await queryClient.invalidateQueries(queryKey);
            setRenderCreateItem(false);
            updateList(newList.id, {
              creators: creators.map(mapCreatorFromFoamToUI),
              name: newList.name,
            });
          },
        },
      );
    },
    [creators, queryKey, mutateCreateList, updateList],
  );

  useKeyup('Escape', () => {
    if (renderCreateItem) {
      setRenderCreateItem(false);
    }
  });

  return {
    creators: creators.map(mapCreatorFromFoamToUI),
    fetchNextPage: handleFetchNextPage,
    hasNextPage,
    isAgencyListEnabled,
    isInitialLoading,
    isLoading: isFetchingNextPage || isFetching || isLoading,
    isOpen,
    isSubmittingCreateList: isCreateListLoading || isRefetching,
    listQuery,
    lists,
    onClose: handleClose,
    onSubmitCreateList: handleSubmitCreateList,
    onSuccess,
    renderCreateItem,
    setListQuery,
    setRenderCreateItem,
    setStep,
    setType,
    step,
    total: data?.totalItems ?? 0,
    type,
  };
};

export const [SavedListsModalProvider, useSavedListsModalProvider] =
  createContext<ReturnType<typeof useSavedListsModal>>();
